/**
 * 75:2's 'Alcance ao longo do tempo' card: an area chart over `api`'s
 * `reading/reach-series` route, plus its footer stat row.
 *
 * The frame's third footer stat, "Publicações no dia", has no producing
 * field on `AccountReachSeriesOut` (day + reach value only, no per-day post
 * count) -- rendered `Absent` rather than computed from a second query's
 * unrelated total, same shape as `MonitoringOverview`'s documented gaps.
 */

import type { ReactNode } from 'react';
import { Area, AreaChart, Bar, BarChart, LabelList, XAxis } from 'recharts';

import { Absent } from '../../../../chrome/measure';
import { EmptyState } from '../../../../chrome/state/EmptyState';
import { QueryStates } from '../../../../chrome/state/QueryStates';
import { getLocale } from '../../../../i18n';
import { compact } from '../../../../i18n/format';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../../ui/chart';
import { Skeleton } from '../../../../ui/skeleton';
import { useAccountReachSeries, type ReadingWindow } from '../api/queries';
import type { AccountReachSeries } from '../api/schemas';
import { am } from '../messages';

// An area needs three days to read as a trend; fewer draw as labelled bars instead of a lone dot.
const MIN_TREND_DAYS = 3;

const dayLabel = (day: string): string =>
  new Intl.DateTimeFormat(getLocale(), { day: '2-digit', month: '2-digit' }).format(
    new Date(`${day}T00:00:00Z`),
  );

function isEmptySeries(data: AccountReachSeries): boolean {
  return data.days.length === 0;
}

function ReachOverTimeBody(): ReactNode {
  return (
    <div className="space-y-4">
      <Skeleton aria-hidden className="h-40 w-full" />
      <div className="flex gap-8">
        <Skeleton className="h-10 w-20" />
        <Skeleton className="h-10 w-20" />
        <Skeleton className="h-10 w-20" />
      </div>
    </div>
  );
}

export function ReachOverTimeSkeleton(): ReactNode {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-52" />
        <Skeleton className="h-4 w-72" />
      </CardHeader>
      <CardContent>
        <ReachOverTimeBody />
      </CardContent>
    </Card>
  );
}

function Chart(props: { data: AccountReachSeries }): ReactNode {
  const locale = getLocale();
  const { days } = props.data;
  const points = days.map((point) => ({
    at: Date.parse(`${point.day}T00:00:00Z`),
    value: point.value,
  }));
  // No initial accumulator: `days` is non-empty here (`isEmptySeries` routes an empty series to
  // the `empty` branch before `Chart` ever renders), and the no-seed form types as `T`, never
  // `T | undefined`, matching that guarantee instead of indexing `days[0]` past it.
  const best = days.reduce((max, point) => (point.value > max.value ? point : max));
  const average = days.reduce((sum, point) => sum + point.value, 0) / days.length;

  const config = {
    value: { label: am('reach.title'), color: 'var(--primary-ink)' },
  } satisfies ChartConfig;

  return (
    <>
      <ChartContainer
        config={config}
        role="img"
        aria-label={am('reach.chart')}
        initialDimension={{ width: 700, height: 160 }}
        className="aspect-auto h-40 w-full rounded-md focus-within:ring-2 focus-within:ring-ring"
      >
        {days.length < MIN_TREND_DAYS ? (
          <BarChart data={days} margin={{ top: 20 }}>
            <XAxis dataKey="day" tickLine={false} axisLine={false} tickFormatter={dayLabel} />
            <Bar
              dataKey="value"
              fill="var(--color-value)"
              radius={4}
              maxBarSize={56}
              isAnimationActive={false}
            >
              <LabelList
                dataKey="value"
                position="top"
                className="fill-foreground text-caption"
                formatter={(value: unknown) =>
                  typeof value === 'number' ? compact(value, locale) : ''
                }
              />
            </Bar>
          </BarChart>
        ) : (
          <AreaChart data={points}>
            <XAxis
              dataKey="at"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickLine={false}
              axisLine={false}
              padding={{ left: 12, right: 12 }}
              tickFormatter={(at: number) => dayLabel(new Date(at).toISOString().slice(0, 10))}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(_label, payload) => {
                    const instant: unknown = payload[0]?.payload?.at;
                    return typeof instant === 'number'
                      ? dayLabel(new Date(instant).toISOString().slice(0, 10))
                      : null;
                  }}
                  formatter={(value) => (typeof value === 'number' ? compact(value, locale) : '')}
                />
              }
            />
            <Area
              dataKey="value"
              type="monotone"
              stroke="var(--color-value)"
              fill="var(--color-value)"
              fillOpacity={0.15}
              strokeWidth={2}
              connectNulls={false}
              isAnimationActive={false}
            />
          </AreaChart>
        )}
      </ChartContainer>
      <div className="flex flex-wrap gap-8">
        <div className="space-y-1">
          <p className="text-caption text-muted-foreground">{am('reach.bestDay')}</p>
          <p data-numeric className="text-body-sm font-medium">
            {compact(best.value, locale)}
            {/* eslint-disable-next-line no-restricted-syntax -- middot separator, identical in both locales */}
            {' · '}
            {dayLabel(best.day)}
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-caption text-muted-foreground">{am('reach.dailyAverage')}</p>
          <p data-numeric className="text-body-sm font-medium">
            {compact(average, locale)}
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-caption text-muted-foreground">{am('reach.postsPerDay')}</p>
          <p className="text-body-sm font-medium">
            <Absent reason="not_measured" />
          </p>
        </div>
      </div>
    </>
  );
}

export function ReachOverTime(props: {
  campaignId: string;
  trackedAccountId: string;
  window: ReadingWindow;
}): ReactNode {
  const query = useAccountReachSeries(props.campaignId, props.trackedAccountId, props.window);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">{am('reach.title')}</CardTitle>
        <p className="text-body-sm text-muted-foreground">{am('reach.subtitle')}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <QueryStates
          query={query}
          isEmpty={isEmptySeries}
          loading={<ReachOverTimeBody />}
          empty={
            <EmptyState
              title={am('reach.empty.title')}
              description={am('reach.empty.description')}
            />
          }
        >
          {(data) => <Chart data={data} />}
        </QueryStates>
      </CardContent>
    </Card>
  );
}
